import { createRoot } from 'react-dom/client';
import * as Sentry from '@sentry/react';
import '../styles/index.css';
import App from '../App';
import { captureException } from '../lib/sentry';

// Initialize Sentry
const sentryDsn =
  document.querySelector('meta[name="sentry-dsn"]')?.getAttribute('content') ||
  import.meta.env.VITE_SENTRY_DSN;
if (sentryDsn) {
  Sentry.init({
    dsn: sentryDsn,
    environment: import.meta.env.MODE,
    integrations: [Sentry.browserTracingIntegration()],
    // Capture 10% of transactions for performance monitoring
    tracesSampleRate: parseFloat(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE || '0.1'),
  });
}

// Global error handler for uncaught JavaScript errors
window.onerror = (message, source, lineno, colno, error) => {
  captureException(error || new Error(String(message)), {
    source: 'window.onerror',
    errorSource: source,
    line: lineno,
    column: colno,
  });
  return false; // Let the error propagate to the console
};

// Global handler for unhandled promise rejections
window.onunhandledrejection = (event: PromiseRejectionEvent) => {
  captureException(event.reason || new Error('Unhandled promise rejection'), {
    source: 'unhandledrejection',
  });
};

const container = document.getElementById('root');
if (container) {
  const root = createRoot(container);
  root.render(<App />);
}

// PWA Service Worker Registration
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker
      .register('/service-worker')
      .then((registration) => {
        console.log('ServiceWorker registered:', registration.scope);
      })
      .catch((error) => {
        captureException(error, { source: 'serviceWorker' });
      });
  });
}
